import { useEffect, useRef, useState } from 'react';
import jsVectorMap from 'jsvectormap';
import '../maps/us-aea-en';

const STATE_CODES = {
    'Alabama': 'US-AL', 'Alaska': 'US-AK', 'Arizona': 'US-AZ', 'Arkansas': 'US-AR',
    'California': 'US-CA', 'Colorado': 'US-CO', 'Connecticut': 'US-CT', 'Delaware': 'US-DE',
    'Florida': 'US-FL', 'Georgia': 'US-GA', 'Hawaii': 'US-HI', 'Idaho': 'US-ID',
    'Illinois': 'US-IL', 'Indiana': 'US-IN', 'Iowa': 'US-IA', 'Kansas': 'US-KS',
    'Kentucky': 'US-KY', 'Louisiana': 'US-LA', 'Maine': 'US-ME', 'Maryland': 'US-MD',
    'Massachusetts': 'US-MA', 'Michigan': 'US-MI', 'Minnesota': 'US-MN', 'Mississippi': 'US-MS',
    'Missouri': 'US-MO', 'Montana': 'US-MT', 'Nebraska': 'US-NE', 'Nevada': 'US-NV',
    'New Hampshire': 'US-NH', 'New Jersey': 'US-NJ', 'New Mexico': 'US-NM', 'New York': 'US-NY',
    'North Carolina': 'US-NC', 'North Dakota': 'US-ND', 'Ohio': 'US-OH', 'Oklahoma': 'US-OK',
    'Oregon': 'US-OR', 'Pennsylvania': 'US-PA', 'Rhode Island': 'US-RI', 'South Carolina': 'US-SC',
    'South Dakota': 'US-SD', 'Tennessee': 'US-TN', 'Texas': 'US-TX', 'Utah': 'US-UT',
    'Vermont': 'US-VT', 'Virginia': 'US-VA', 'Washington': 'US-WA', 'West Virginia': 'US-WV',
    'Wisconsin': 'US-WI', 'Wyoming': 'US-WY',
};

const STATE_CENTROIDS = {
    'Alabama': [32.81, -86.79], 'Alaska': [61.37, -152.40], 'Arizona': [33.73, -111.43],
    'Arkansas': [34.97, -92.37], 'California': [36.12, -119.68], 'Colorado': [39.06, -105.31],
    'Connecticut': [41.60, -72.76], 'Delaware': [39.32, -75.51], 'Florida': [27.77, -81.69],
    'Georgia': [33.04, -83.64], 'Hawaii': [21.09, -157.50], 'Idaho': [44.24, -114.48],
    'Illinois': [40.35, -88.99], 'Indiana': [39.85, -86.26], 'Iowa': [42.01, -93.21],
    'Kansas': [38.53, -96.73], 'Kentucky': [37.67, -84.67], 'Louisiana': [31.17, -91.87],
    'Maine': [44.69, -69.38], 'Maryland': [39.06, -76.80], 'Massachusetts': [42.23, -71.53],
    'Michigan': [43.33, -84.54], 'Minnesota': [45.69, -93.90], 'Mississippi': [32.74, -89.68],
    'Missouri': [38.46, -92.29], 'Montana': [46.92, -110.45], 'Nebraska': [41.13, -98.27],
    'Nevada': [38.31, -117.06], 'New Hampshire': [43.45, -71.56], 'New Jersey': [40.30, -74.52],
    'New Mexico': [34.84, -106.25], 'New York': [42.17, -74.95], 'North Carolina': [35.63, -79.81],
    'North Dakota': [47.53, -99.78], 'Ohio': [40.39, -82.76], 'Oklahoma': [35.57, -96.93],
    'Oregon': [44.57, -122.07], 'Pennsylvania': [40.59, -77.21], 'Rhode Island': [41.68, -71.51],
    'South Carolina': [33.86, -80.95], 'South Dakota': [44.30, -99.44], 'Tennessee': [35.75, -86.69],
    'Texas': [31.05, -97.56], 'Utah': [40.15, -111.86], 'Vermont': [44.05, -72.71],
    'Virginia': [37.77, -78.17], 'Washington': [47.40, -121.49], 'West Virginia': [38.49, -80.95],
    'Wisconsin': [44.27, -89.62], 'Wyoming': [42.76, -107.30],
};

export default function OrgMap({ markers }) {
    const containerRef = useRef(null);
    const mapInstance = useRef(null);
    const activeMarker = useRef(null);
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, html: '' });

    useEffect(() => {
        if (!containerRef.current || !markers || markers.length === 0) return;

        containerRef.current.innerHTML = '';

        const selectedRegions = [];
        const byCoords = {};

        for (const org of markers) {
            const code = STATE_CODES[org.state];
            const fallback = STATE_CENTROIDS[org.state];
            const coords = (org.lat && org.lng) ? [org.lat, org.lng] : fallback;

            if (code && !selectedRegions.includes(code)) {
                selectedRegions.push(code);
            }

            if (coords) {
                const key = `${coords[0]},${coords[1]}`;
                if (!byCoords[key]) {
                    byCoords[key] = { coords, state: org.state, orgs: [] };
                }
                byCoords[key].orgs.push({
                    name: org.name,
                    city: org.city,
                    email: org.email,
                    url: org.url,
                    url2: org.url2,
                });
            }
        }

        const mapMarkers = Object.values(byCoords);

        const buildTooltipHtml = (m) => {
            const orgLines = m.orgs.map(o => {
                const contact = [
                    o.city || '',
                    o.email ? `<a href="mailto:${o.email}" style="color:#adf">${o.email}</a>` : '',
                    o.url ? `<a href="${o.url}" target="_blank" style="color:#adf">${o.url}</a>` : '',
                    o.url2 ? `<a href="${o.url2}" target="_blank" style="color:#adf">${o.url2}</a>` : '',
                ].filter(Boolean).join('<br/>');
                return `<span style="text-decoration:underline;font-weight:bold">${o.name}</span>`
                    + (contact ? '<br/>' + contact : '');
            }).join('<br/><br/>');
            return `<strong>${m.state}</strong><br/><br/>${orgLines}`;
        };

        mapInstance.current = new jsVectorMap({
            selector: containerRef.current,
            map: 'us_aea_en',
            selectedRegions,
            regionsSelectable: false,
            showTooltip: false,
            regionStyle: {
                initial: { fill: 'rgba(100, 100, 150, 0.6)', stroke: 'rgba(255,255,255,0.1)' },
                selected: { fill: 'rgba(100, 210, 130, 0.75)' },
            },
            markers: mapMarkers,
            markerStyle: {
                initial: { fill: 'white', stroke: 'rgba(0,0,0,0.3)', r: 5 },
                hover: { fill: '#ffd700', r: 7 },
            },
            zoomButtons: false,
            zoomOnScroll: false,
            onMarkerClick(event, index) {
                if (activeMarker.current === index) {
                    setTooltip(t => ({ ...t, visible: false }));
                    activeMarker.current = null;
                } else {
                    activeMarker.current = index;
                    const rect = containerRef.current.parentElement.getBoundingClientRect();
                    setTooltip({
                        visible: true,
                        x: event.clientX - rect.left + 12,
                        y: event.clientY - rect.top + 12,
                        html: buildTooltipHtml(mapMarkers[index]),
                    });
                }
            },
        });

        return () => {
            if (mapInstance.current) {
                if (!mapInstance.current._tooltip) {
                    mapInstance.current._tooltip = { dispose: () => {} };
                }
                mapInstance.current.destroy();
                mapInstance.current = null;
            }
            activeMarker.current = null;
        };
    }, [markers]);

    const handleMapClick = (e) => {
        if (!e.target.closest('.jvm-marker') && activeMarker.current !== null) {
            setTooltip(t => ({ ...t, visible: false }));
            activeMarker.current = null;
        }
    };

    return (
        <div style={{ position: 'relative', width: '100%', marginTop: '40px' }}>
            <div
                ref={containerRef}
                style={{ width: '100%', height: '500px' }}
                onClick={handleMapClick}
            />
            {tooltip.visible && (
                <div
                    style={{
                        position: 'absolute',
                        top: tooltip.y,
                        left: tooltip.x,
                        background: '#2e2e2e',
                        color: '#fff',
                        padding: '10px 14px',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        lineHeight: '1.5',
                        maxWidth: '280px',
                        zIndex: 9999,
                        pointerEvents: 'auto',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltip.html }}
                />
            )}
        </div>
    );
}
